<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div>
                    <h1 class="text-left text-h5 font-weight-bold">Places</h1>
                    <p class="text-body-2 text-medium-emphasis text-left mb-0">
                        Save spots like Home or School. Everyone in your circle sees them on the map.
                    </p>
                </div>
                <v-btn v-if="!authStore.isMinor" color="primary" prepend-icon="mdi-map-marker-plus"
                       @click="openAdd">
                    Add place
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card variant="outlined" rounded="lg">
                    <v-table hover>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Alerts</th>
                            <th class="text-center" style="width: 120px">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="3" class="text-center pa-8">
                                    <v-progress-circular color="primary" indeterminate size="32"/>
                                    <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading places...</p>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="places.length">
                            <tr v-for="place in places" :key="place.id" class="text-left">
                                <td class="font-weight-medium">
                                    <v-icon size="18" color="primary" class="mr-2">mdi-map-marker</v-icon>
                                    {{ place.label ?? place.name }}
                                </td>
                                <td>
                                    <v-chip v-if="alertLabel(place)" size="small" variant="tonal" color="primary">
                                        {{ alertLabel(place) }}
                                    </v-chip>
                                    <span v-else class="text-medium-emphasis text-body-2">Saved place</span>
                                </td>
                                <td class="text-center">
                                    <v-btn v-if="!authStore.isMinor" icon="mdi-pencil-outline" size="small"
                                           variant="text" color="primary" title="Edit"
                                           @click="openEdit(place)"/>
                                    <v-btn v-if="!authStore.isMinor" icon="mdi-delete-outline" size="small"
                                           variant="text" color="error" title="Delete"
                                           @click="deletePlace(place)"/>
                                    <span v-else class="text-medium-emphasis text-caption">View only</span>
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr>
                                <td colspan="3" class="text-center pa-10">
                                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-map-marker-outline</v-icon>
                                    <p class="text-body-1 text-medium-emphasis mb-1">No places yet</p>
                                    <p class="text-body-2 text-medium-emphasis mb-3">
                                        Save places like Home or School. Everyone sees them on the map,
                                        and you can be alerted when trackers arrive or leave.
                                    </p>
                                    <v-btn v-if="!authStore.isMinor" color="primary"
                                           prepend-icon="mdi-map-marker-plus" size="small" @click="openAdd">
                                        Add place
                                    </v-btn>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination v-model="currentPage" :length="totalPages" class="my-4"
                              density="compact" @update:model-value="fetchPlaces"/>
            </v-col>
        </v-row>
    </v-container>

    <PlaceDialog v-if="dialogSwitch" v-model="dialogSwitch" :place="editingPlace"
                 :initial-point="initialPoint" @placeCreated="onPlaceCreated"/>
</template>

<script setup>
import {ref, onMounted} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import PlaceDialog from "@/components/dialogs/PlaceDialog.vue";

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();

// A "Place" is a geofence template (monitoring/geofence-templates) - the single
// place backend the app and the map "At X" label already use. The template's
// enter_or_exit records whether arrive/leave alerting is on (the dialog also
// attaches the monitoring config that actually fires it).
const places = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const dialogSwitch = ref(false);
// The place being edited (null = the dialog is in "add new" mode).
const editingPlace = ref(null);
// A {lat,lng} seed from a "save place here" deep-link, else null.
const initialPoint = ref(null);

const alertLabel = (place) =>
    (Number(place.enter_or_exit) || 0) > 0 ? 'Alerts on arrival & departure' : null;

const openAdd = () => {
    editingPlace.value = null;
    initialPoint.value = null;
    dialogSwitch.value = true;
};

const openEdit = (place) => {
    editingPlace.value = place;
    initialPoint.value = null;
    dialogSwitch.value = true;
};

const onPlaceCreated = () => {
    dialogSwitch.value = false;
    editingPlace.value = null;
    initialPoint.value = null;
    fetchPlaces();
};

// The list endpoint returns a Laravel paginator, so items live at
// response.data.data.data; fall back to response.data.data if the API returns a
// bare array instead.
const extractItems = (response) => {
    const data = response?.data?.data;
    if (data && Array.isArray(data.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
};

const fetchPlaces = () => {
    loading.value = true;
    return send('get', 'monitoring/geofence-templates', {page: currentPage.value})
        .then((response) => {
            places.value = extractItems(response);
            const meta = response?.data?.data;
            totalPages.value = meta?.last_page ?? response?.data?.last_page ?? 1;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error);
            loading.value = false;
            showSnackbar('Your places could not be loaded.', 'error');
        });
};

// The geofence alert config attached to [templateId] across all config pages,
// or null. Needed to delete a place: the template DELETE is refused while a
// config is attached, and deleting the config cascade-removes the template.
const findConfigForPlace = async (templateId) => {
    let page = 1;
    let last = 1;
    try {
        do {
            const resp = await send('get', 'monitoring/configs', {page});
            const pg = resp?.data?.data;
            const hit = (pg?.data ?? []).find(
                (c) => c.monitoring_alertable_type === 'App\\Models\\MonitoringGeofenceTemplate'
                    && Number(c.monitoring_alertable_id) === Number(templateId),
            );
            if (hit) return hit;
            last = Number(pg?.last_page) || page;
            page++;
        } while (page <= last && page <= 20);
    } catch {
        return null;
    }
    return null;
};

const deletePlace = async (place) => {
    const label = place.label ?? place.name ?? 'this place';
    if (!confirm(`Remove "${label}"? This cannot be undone.`)) return;
    try {
        // An attached alert config blocks the template DELETE, so remove it first
        // (deleting the config cascade-deletes the template + its group). Only a
        // config-less place is deleted via the template endpoint.
        const config = await findConfigForPlace(place.id);
        const response = config
            ? await send('delete', `monitoring/configs/${config.id}`)
            : await send('delete', `monitoring/geofence-templates/${place.id}`);
        if (response.data.success === false) {
            showSnackbar(response.data.message || 'Could not remove that place.', 'error');
            return;
        }
        places.value = places.value.filter((p) => p.id !== place.id);
        showSnackbar('Place removed.');
    } catch (error) {
        showSnackbar(error.response?.data?.message || 'Could not remove that place.', 'error');
    }
};

// Open the dialog seeded from a "save place here" deep-link (?lat&lng[&name]).
const handleSavePlaceQuery = () => {
    const {lat, lng} = route.query;
    const latN = Number(lat);
    const lngN = Number(lng);
    const valid = Number.isFinite(latN) && Number.isFinite(lngN)
        && latN >= -90 && latN <= 90 && lngN >= -180 && lngN <= 180;
    if (!valid) return;
    editingPlace.value = null;
    initialPoint.value = {lat: latN, lng: lngN};
    dialogSwitch.value = true;
    router.replace({query: {}});
};

onMounted(() => {
    fetchPlaces().finally(handleSavePlaceQuery);
});
</script>
